import { useUIStore } from '@/config/stores/ui-store';
import { useMessageInputDropzone } from '@/features/messages/hooks/use-message-input-dropzone';
import { usePasteFiles } from '@/features/messages/hooks/use-paste-files';
import { useState } from 'react';
import { categorizeAttachments } from '../components/message-input/message-input.sender';
import type { AudioAttachment } from '../models/attachment';
import { MessageContentType } from '../models/message-content-type';
import { useMessageSend } from '../queries/use-message-send';

export const MEDIA_CHUNK_SIZE = 4;
export const AUDIO_VIDEO_CHUNK_SIZE = 1;
export const FILE_CHUNK_SIZE = 5;

export const useMessageInput = (convId: Nullable<string>) => {
  const [value, setValue] = useState('');

  const { mutateAsync: send } = useMessageSend(convId);
  const { getReplyMessage, clearReplyMessage } = useUIStore();
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
    attachments,
    setAttachments,
    attachmentMode,
    openWithMode
  } = useMessageInputDropzone();
  const { handlePaste } = usePasteFiles(attachments, setAttachments);

  const handleSendMsg = async (audio?: AudioAttachment) => {
    if (!convId) return;
    const { medias, files } = categorizeAttachments(attachments);
    let content: Nullable<string> = value.trim();
    let replyId = getReplyMessage(convId || '')?.messageId || null;

    // Send medias in batches of 4
    if (medias.length > 0) {
      const imageChunks = [];
      for (let i = 0; i < medias.length; i += MEDIA_CHUNK_SIZE) {
        imageChunks.push(medias.slice(i, i + MEDIA_CHUNK_SIZE));
      }

      for (const chunk of imageChunks) {
        send({
          contentType: MessageContentType.COMPOSE,
          content: content,
          attachments: chunk,
          parentMessageId: replyId
        });
        content = null;
        replyId = null;
      }
    }

    if (audio?.file) {
      send({
        contentType: MessageContentType.COMPOSE,
        content: content,
        attachments: [audio],
        parentMessageId: replyId
      });
    }

    if (files.length > 0) {
      const fileChunks = [];
      for (let i = 0; i < files.length; i += FILE_CHUNK_SIZE) {
        fileChunks.push(files.slice(i, i + FILE_CHUNK_SIZE));
      }

      for (const chunk of fileChunks) {
        send({
          contentType: MessageContentType.COMPOSE,
          content: content,
          attachments: chunk,
          parentMessageId: replyId
        });
        content = null;
        replyId = null;
      }
    }

    // If no attachments, send as text message
    if (attachments.length === 0 && content) {
      send({
        contentType: MessageContentType.TEXT,
        content: content,
        parentMessageId: replyId
      });
      content = null;
      replyId = null;
    }

    setAttachments([]);
    setValue('');
    clearReplyMessage();
  };

  /**
   * Handles key up events for sending message
   */
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        return;
      }
      event.preventDefault();
      if (value.trim() !== '' || attachments.length > 0) {
        handleSendMsg();
      }
    }
  };

  /**
   * Checks if send button should be disabled
   */
  const isSendDisabled = (disabled: boolean) => {
    return disabled || (value?.trim() === '' && attachments.length === 0);
  };

  return {
    // State
    value,
    setValue,
    attachments,
    setAttachments,
    attachmentMode,

    // Dropzone props
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
    openWithMode,

    // Actions
    handleSendMsg,
    onKeyDown,
    handlePaste,

    // Status
    isSendDisabled
  };
};
